import { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import { authAdapter } from "./adapter";
import type { Credentials, PermissionRule, Session } from "./types";

interface AuthContextValue {
	status: "authenticated" | "unauthenticated";
	session: Session | null;
	can: (rule: PermissionRule) => boolean;
	login: (credentials: Credentials) => Promise<Session>;
	logout: () => Promise<void>;
	refresh: () => Promise<Session | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const state = useSyncExternalStore(authAdapter.subscribe, authAdapter.getState);

	const value = useMemo<AuthContextValue>(
		() => ({
			status: state.status,
			session: state.session,
			can: authAdapter.can,
			login: authAdapter.login,
			logout: authAdapter.logout,
			refresh: authAdapter.refresh,
		}),
		[state],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
	const context = useContext(AuthContext);
	if (!context) throw new Error("useAuth must be used inside AuthProvider");
	return context;
}
